import React, { useCallback, useEffect, useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ComposedChart } from "recharts";
import { Cloud, RefreshCw, Wind, Thermometer, Sun, CloudRain, AlertTriangle } from "lucide-react";
import { fmt, shortDate } from "../lib/format";

const TT = { background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 };

export default function Weather() {
  const { currentSite } = useSite();
  const [rows, setRows] = useState([]);
  const [activeSource, setActiveSource] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    if (!currentSite) return;
    const { data: payload } = await api.get(`/sites/${currentSite.id}/weather/daily?days=60`);
    setRows(payload.rows || []);
    setActiveSource(payload.active_source || null);
  }, [currentSite]);
  useEffect(() => { reload(); }, [reload]);

  const sync = async () => {
    setSyncing(true); setErr(null);
    try { await api.post(`/sites/${currentSite.id}/weather/sync?days=60`); await reload(); }
    catch (e) { setErr(e.userMessage); }
    finally { setSyncing(false); }
  };

  const chart = rows.map((r) => ({
    label: shortDate(r.date), date: r.date,
    rain: r.rain_mm || 0,
    temp_max: r.temp_max_c, temp_min: r.temp_min_c, temp_avg: r.temp_avg_c,
    wind: r.wind_speed_ms, gust: r.wind_gust_ms,
    uv: r.uv_index, radiation: r.solar_radiation_wm2,
  }));

  return (
    <div className="space-y-8 pb-12" data-testid="weather-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="label-tiny mb-1.5 flex items-center gap-2"><Cloud className="w-3.5 h-3.5" /> Weather</div>
          <h1 className="font-heading text-3xl font-semibold">Weather</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="weather-source-label">
            {activeSource?.label || "Open-Meteo daily reanalysis"} · {rows.length} days
            {activeSource?.mixed && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#F59E0B]">mixed</span>}
          </p>
        </div>
        <button onClick={sync} disabled={syncing} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs hover:bg-secondary disabled:opacity-50" data-testid="weather-sync">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync 60 days"}
        </button>
      </header>

      {err && <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5">{err}</div>}

      {rows.length === 0 ? (
        <div className="card-base p-10 text-center">
          <Cloud className="w-10 h-10 mx-auto text-muted-foreground" />
          <div className="mt-3 font-medium">No weather data yet</div>
          <div className="text-sm text-muted-foreground mt-1">
            Tap "Sync 60 days" to pull from Open-Meteo (no API key), or configure your Wunderground PWS in Data Sources for hyper-local rain.
          </div>
        </div>
      ) : (
        <>
          <ChartCard title="Rain (mm/day)">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <Tooltip contentStyle={TT} />
                <Bar dataKey="rain" fill="#60A5FA" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Temperature (°C)" Icon={Thermometer}>
            <ResponsiveContainer>
              <LineChart data={chart}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <Tooltip contentStyle={TT} />
                <Line type="monotone" dataKey="temp_max" name="Max" stroke="#FB923C" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="temp_min" name="Min" stroke="#60A5FA" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="temp_avg" name="Avg" stroke="#FAFAFA" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Wind (m/s)" Icon={Wind}>
            <div className="text-[11px] text-muted-foreground -mt-2 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-[#F59E0B]" />
              Wind data is indicative only — useful for future small-turbine feasibility analysis.
            </div>
            <ResponsiveContainer>
              <ComposedChart data={chart}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <Tooltip contentStyle={TT} />
                <Line type="monotone" dataKey="gust" name="Gust" stroke="#FAFAFA" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="wind" name="Wind" stroke="#3B82F6" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="UV index & solar radiation (W/m²)" Icon={Sun}>
            <ResponsiveContainer>
              <LineChart data={chart}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <YAxis yAxisId="uv" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <YAxis yAxisId="rad" orientation="right" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <Tooltip contentStyle={TT} />
                <Line yAxisId="uv" type="monotone" dataKey="uv" name="UV" stroke="#FBBF24" strokeWidth={2} dot={false} />
                <Line yAxisId="rad" type="monotone" dataKey="radiation" name="Radiation W/m²" stroke="#F59E0B" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}

      {/* Table */}
      {rows.length > 0 && (
        <section className="card-base overflow-hidden">
          <div className="px-5 py-3 label-tiny border-b border-border">Daily log · last {Math.min(rows.length, 30)} rows</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="weather-table">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-5 py-2.5">Date</th>
                  <th className="text-right px-3">Rain</th>
                  <th className="text-right px-3">Tmin</th>
                  <th className="text-right px-3">Tmax</th>
                  <th className="text-right px-3">Wind</th>
                  <th className="text-right px-3">Gust</th>
                  <th className="text-right px-3">Dir</th>
                  <th className="text-right px-3">UV</th>
                  <th className="text-right px-5">Radiation</th>
                </tr>
              </thead>
              <tbody className="font-mono text-[12px]">
                {[...rows].reverse().slice(0, 30).map((r) => (
                  <tr key={r.date} className="border-b border-border/60 hover:bg-secondary/30">
                    <td className="px-5 py-2 font-sans">{shortDate(r.date)}</td>
                    <td className="text-right px-3 text-[#60A5FA]">{fmt(r.rain_mm, 1)}</td>
                    <td className="text-right px-3">{fmt(r.temp_min_c, 1)}</td>
                    <td className="text-right px-3">{fmt(r.temp_max_c, 1)}</td>
                    <td className="text-right px-3">{fmt(r.wind_speed_ms, 1)}</td>
                    <td className="text-right px-3">{fmt(r.wind_gust_ms, 1)}</td>
                    <td className="text-right px-3 text-muted-foreground">{fmt(r.wind_direction_deg, 0)}°</td>
                    <td className="text-right px-3">{fmt(r.uv_index, 1)}</td>
                    <td className="text-right px-5">{fmt(r.solar_radiation_wm2, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function ChartCard({ title, children, Icon }) {
  return (
    <section className="card-base p-5">
      <div className="label-tiny mb-3 flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />}{title}</div>
      <div className="h-64">{children}</div>
    </section>
  );
}
